// Reads a Fortress card's printed rules text into the parts the card face lays
// out: the Action (its text plus the token code that ends it — "1TR", "3T") and
// the Passive, whose common phrasings ("Gain +2 banked energy", "Gain +1 action
// per turn", "Gain +2 attack & defend range"…) become icon lines. Anything the
// parser doesn't recognise stays text, so an unusual sentence is never lost or
// mis-drawn — only exact, whole-sentence matches become icons.
//
// Catalogue "49"+ regular cards print two lines:
//   "Action: <text> <N>T[R][.]"   N tokens = uses; R = they clear each round
//   "Passive: <text>"
// Older printings (and win cards) are a single paragraph → `plain`.

export type ResourceKey = "minerals" | "food" | "vigor" | "obsidian";
export const ALL_RESOURCES: readonly ResourceKey[] = ["minerals", "food", "vigor", "obsidian"];

/** What a range bonus applies to: a piece (building range) or a barracks
 * posture. */
export type RangeTarget = "scout" | "barracks" | "mine" | "bridge" | "attack" | "defend";
export const ALL_PIECES: readonly RangeTarget[] = ["scout", "barracks", "mine", "bridge"];

/** The Action's uses: `count` token slots; `perRound` = they clear at the end
 * of every round (else they stay for the whole game). */
export interface TokenSpec {
  readonly count: number;
  readonly perRound: boolean;
}

export type IconLine =
  /** +amount of ONE of `resources` (a slash between icons = pick one), into
   * the bank (one-time icons) or as production (permanent icons). */
  | {
      readonly kind: "gain";
      readonly amount: number;
      readonly resources: readonly ResourceKey[];
      readonly production: boolean;
    }
  /** +amount action points every turn. */
  | { readonly kind: "actions"; readonly amount: number }
  /** Range bonuses: each group's amount applies to every target after it. */
  | {
      readonly kind: "range";
      readonly groups: readonly { readonly amount: number; readonly targets: readonly RangeTarget[] }[];
    };

export type PassivePart =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "icons"; readonly line: IconLine; readonly text: string }
  /** "X. Or Y." — pick one of the options. */
  | { readonly kind: "choice"; readonly options: readonly PassivePart[] };

export interface ParsedCardText {
  /** Printed "Restriction: …" lines (build constraints), in order. */
  readonly restrictions?: readonly string[];
  readonly action?: {
    readonly text: string;
    readonly tokens?: TokenSpec;
    /** Pick-one actions ("X. Or Y."): the options, "Or" dropped. Absent when
     * the action has one form. */
    readonly options?: readonly string[];
  };
  readonly passive?: readonly PassivePart[];
  /** Pre-"49" single-paragraph text (win cards, older printings). */
  readonly plain?: string;
}

const TOKEN_CODE = /\s*(\d+)\s*T(R)?\.?\s*$/;

/** Split a card's description into Action / Passive parts. */
export function parseCardText(description: string): ParsedCardText {
  const all = description
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);
  const RESTRICTION = /^restrictions?:\s*/i;
  const restrictionLines = all
    .filter((l) => RESTRICTION.test(l))
    .map((l) => l.replace(RESTRICTION, ""));
  const restrictions = restrictionLines.length ? { restrictions: restrictionLines } : {};
  const lines = all.filter((l) => !RESTRICTION.test(l));
  const actionLine = lines.find((l) => /^action:/i.test(l));
  const passiveLine = lines.find((l) => /^passive:/i.test(l));
  if (!actionLine && !passiveLine) return { ...restrictions, plain: lines.join("\n") };

  let action: ParsedCardText["action"];
  if (actionLine) {
    let text = actionLine.replace(/^action:\s*/i, "");
    let tokens: TokenSpec | undefined;
    const m = TOKEN_CODE.exec(text);
    if (m) {
      tokens = { count: Number(m[1]), perRound: m[2] !== undefined };
      text = text.slice(0, m.index).trim();
    }
    if (text && !/[.!?]$/.test(text)) text += ".";
    // "Untap 1 of your barracks. Or perform an action without consuming an
    // action." → two options; sentences between "Or"s stay with their option.
    const options: string[] = [];
    for (const sentence of splitSentences(text)) {
      if (/^or\s+/i.test(sentence) && options.length > 0) {
        options.push(capitalise(sentence.replace(/^or\s+/i, "")));
      } else if (options.length === 0) {
        options.push(sentence);
      } else {
        options[options.length - 1] += ` ${sentence}`;
      }
    }
    action = { text, tokens, ...(options.length > 1 ? { options } : {}) };
  }

  let passive: PassivePart[] | undefined;
  if (passiveLine) {
    passive = [];
    for (const sentence of splitSentences(passiveLine.replace(/^passive:\s*/i, ""))) {
      const isOr = /^or\s+/i.test(sentence);
      const line = parseIconSentence(sentence);
      const part: PassivePart = line
        ? { kind: "icons", line, text: sentence }
        : (inlineIconChoice(sentence) ?? {
            kind: "text",
            text: isOr ? capitalise(sentence.replace(/^or\s+/i, "")) : sentence,
          });
      const prev = passive[passive.length - 1];
      if (isOr && prev) {
        passive[passive.length - 1] =
          prev.kind === "choice"
            ? { kind: "choice", options: [...prev.options, part] }
            : { kind: "choice", options: [prev, part] };
      } else {
        passive.push(part);
      }
    }
  }
  return { ...restrictions, action, passive };
}

/** Sentences of older one-paragraph printings that state a build restriction
 * ("All buildings … must be built in the highest energy zone", "Can only be
 * built if…") — dropped from the paragraph when the card's restriction section
 * already says it. */
const RESTRICTION_SENTENCE =
  /\b(?:must be built|can only be built|cannot be built|can't be built|must have at least \d+ buildings?|must be in (?:the )?(?:\w+ )?energy zone)\b/i;

export function withoutRestrictionSentences(text: string): string {
  return text
    .split(/\n+/)
    .map((para) =>
      splitSentences(para)
        .filter((s) => !RESTRICTION_SENTENCE.test(s))
        .join(" "),
    )
    .filter(Boolean)
    .join("\n");
}

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+(?=[A-Z])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "Gain +2 banked obsidian or +1 obsidian production." — a pick-one passive
 * printed as ONE sentence: a choice when every "or"-separated half is an icon
 * line, else null (the sentence stays text). Only an "or" before a "+N" (or
 * "gain +N") splits, so a resource list ("minerals, food or energy") never
 * does. */
function inlineIconChoice(sentence: string): PassivePart | null {
  const halves = sentence.split(/\s+or\s+(?=(?:gain\s+)?\+\d)/i);
  if (halves.length < 2) return null;
  const options: PassivePart[] = [];
  for (const half of halves) {
    const line = parseIconSentence(half);
    if (!line) return null;
    options.push({ kind: "icons", line, text: capitalise(half.trim()) });
  }
  return { kind: "choice", options };
}

const RESOURCE_WORD: Record<string, readonly ResourceKey[]> = {
  mineral: ["minerals"],
  minerals: ["minerals"],
  food: ["food"],
  energy: ["vigor"],
  vigor: ["vigor"],
  obsidian: ["obsidian"],
  resource: ALL_RESOURCES,
  resources: ALL_RESOURCES,
  "any resource": ALL_RESOURCES,
  "any resources": ALL_RESOURCES,
};

/** One kind, or a pick-one list of kinds ("minerals, food or energy"). */
const ONE_RESOURCE = "(?:minerals?|food|energy|vigor|obsidian)";
const RESOURCE_LIST = `${ONE_RESOURCE}(?:(?:, or |, | or )${ONE_RESOURCE})*`;

/** The kinds a resource phrase names — one word, or each word of a list. */
function resourcesOf(phrase: string): readonly ResourceKey[] {
  return (
    RESOURCE_WORD[phrase] ?? [
      ...new Set(phrase.split(/, or |, | or /).flatMap((w) => RESOURCE_WORD[w] ?? [])),
    ]
  );
}

const PIECE_WORD: Record<string, RangeTarget> = {
  scout: "scout",
  scouts: "scout",
  barracks: "barracks",
  mine: "mine",
  mines: "mine",
  bridge: "bridge",
  bridges: "bridge",
};

/** One passive sentence → an icon line, or null to keep it as text. */
export function parseIconSentence(sentence: string): IconLine | null {
  const s = sentence
    .trim()
    .replace(/[.!]$/, "")
    .replace(/^or\s+/i, "")
    .toLowerCase()
    .replace(/\s+/g, " ");

  // "Gain" is optional: the sheet also prints the terse "Or +1 resource
  // production" / "+2 banked resources" forms after an "Or".
  // Resources into the bank: "gain +2 banked energy", "gain +3 minerals",
  // "+2 banked minerals, food or energy".
  let m = new RegExp(`^(?:gain )?\\+(\\d+) (?:banked )?(any resources?|resources?|${RESOURCE_LIST})$`).exec(s);
  if (m) return { kind: "gain", amount: Number(m[1]), resources: resourcesOf(m[2]!), production: false };
  // Production: "gain +1 energy production", "gain +1 any resource production",
  // "gain +1 of any production", "+1 resource production",
  // "+1 mineral, food or energy production".
  m = new RegExp(`^(?:gain )?\\+(\\d+) (?:of )?(any resources?|any|resources?|${RESOURCE_LIST}) production$`).exec(s);
  if (m) {
    const res = m[2] === "any" ? ALL_RESOURCES : resourcesOf(m[2]!);
    return { kind: "gain", amount: Number(m[1]), resources: res, production: true };
  }
  // Extra actions: "gain +1 action per turn", "gain 1 extra/additional action per turn".
  m = /^gain \+?(\d+) (?:extra |additional )?actions? per turn$/.exec(s);
  if (m) return { kind: "actions", amount: Number(m[1]) };
  // Ranges: "gain +3 building range and +1 attack & defend range", …
  m = /^gain (\+\d+ .+)$/.exec(s);
  if (m) {
    const groups: { amount: number; targets: RangeTarget[] }[] = [];
    for (const piece of m[1]!.split(/ and (?=\+\d)/)) {
      const g = parseRangeGroup(piece);
      if (!g) return null;
      groups.push(g);
    }
    return { kind: "range", groups };
  }
  return null;
}

function parseRangeGroup(text: string): { amount: number; targets: RangeTarget[] } | null {
  const m = /^\+(\d+) (.+)$/.exec(text.trim());
  if (!m) return null;
  const amount = Number(m[1]);
  const what = m[2]!;
  if (what === "building range") return { amount, targets: [...ALL_PIECES] };
  if (/^attack (?:&|and) defend range$/.test(what)) return { amount, targets: ["attack", "defend"] };
  if (what === "attack range") return { amount, targets: ["attack"] };
  if (what === "defend range") return { amount, targets: ["defend"] };
  // "scout range", "mine building range", "bridge building range"
  let p = /^(scouts?|barracks|mines?|bridges?)(?: building)? range$/.exec(what);
  if (p) return { amount, targets: [PIECE_WORD[p[1]!]!] };
  // "building range for your scouts and bridges"
  p = /^building range for (?:your )?(.+)$/.exec(what);
  if (p) {
    const words = p[1]!.split(/,\s*|\s+and\s+/).map((w) => w.trim());
    const targets = words.map((w) => PIECE_WORD[w]);
    if (targets.every((t): t is RangeTarget => t !== undefined)) return { amount, targets };
  }
  return null;
}
